import React, { useCallback, useEffect, useState } from "react";
import { api, formatDetail } from "../lib/api";
import { toast } from "sonner";
import { Database, RotateCcw } from "lucide-react";

function when(iso) {
  const d = new Date(iso);
  return d.toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function RiderSnapshots({ onRestored }) {
  const [snaps, setSnaps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/riders/snapshots");
      setSnaps(data.snapshots || []);
    } catch (e) { toast.error(formatDetail(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/riders/snapshots");
      toast(`${data.count} rider${data.count === 1 ? "" : "s"} saved to database`);
      await load();
    } catch (e) { toast.error(formatDetail(e)); }
    finally { setSaving(false); }
  }

  async function check(id) {
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/riders/snapshots/${id}/restore`, { confirm: false });
      setPreview({ id, ...data });
    } catch (e) { toast.error(formatDetail(e)); }
    finally { setBusyId(null); }
  }

  async function restore() {
    if (!preview) return;
    setBusyId(preview.id);
    try {
      const { data } = await api.post(`/admin/riders/snapshots/${preview.id}/restore`, { confirm: true });
      toast(`${data.restored} rider${data.restored === 1 ? "" : "s"} restored`);
      setPreview(null);
      onRestored?.();
    } catch (e) { toast.error(formatDetail(e)); }
    finally { setBusyId(null); }
  }

  return (
    <div className="border-t border-border/60 pt-3 space-y-2" data-testid="rider-snapshots">
      <button
        onClick={save}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 bg-text-primary text-bg-primary uppercase tracking-widest text-xs font-black py-3 rounded-lg disabled:opacity-50 active:scale-[0.98]"
        data-testid="rider-snapshot-save-button"
      >
        <Database className="w-4 h-4" /> {saving ? "Saving…" : "Save riders to database"}
      </button>
      <div className="text-[10px] text-text-muted leading-snug">
        Stores a copy of every rider's login and profile inside the database. Restore never overwrites current riders — it only brings back missing ones.
      </div>

      {snaps.length > 0 && (
        <ul className="space-y-1" data-testid="rider-snapshot-list">
          {snaps.map((s) => (
            <li key={s.id} className="flex items-center gap-2 bg-bg-primary rounded-lg px-3 py-2" data-testid={`rider-snapshot-${s.id}`}>
              <div className="flex-1 min-w-0 text-[11px]">
                <span className="font-bold text-text-primary">{s.count} riders</span>
                <span className="text-text-muted"> · {when(s.created_at)} · {s.created_by}</span>
              </div>
              <button
                onClick={() => check(s.id)}
                disabled={busyId === s.id}
                className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-text-primary border border-border rounded-md px-2 py-1 disabled:opacity-50"
                data-testid={`rider-snapshot-restore-${s.id}`}
              >
                <RotateCcw className="w-3 h-3" /> Restore
              </button>
            </li>
          ))}
        </ul>
      )}

      {preview && (
        <div className="bg-bg-primary rounded-lg p-3 space-y-2" data-testid="rider-snapshot-preview">
          <div className="text-[11px] text-text-muted" data-testid="rider-snapshot-counts">
            <span className="text-text-primary font-bold">{preview.restorable.length}</span> missing · will be restored<br />
            <span className="font-bold">{preview.skipped_existing.length}</span> already exist · left untouched
          </div>
          {preview.restorable.length > 0 ? (
            <button
              onClick={restore}
              disabled={busyId === preview.id}
              className="w-full bg-status-cant text-white uppercase tracking-widest text-xs font-black py-2.5 rounded-lg disabled:opacity-50"
              data-testid="rider-snapshot-confirm-button"
            >
              Restore {preview.restorable.length} rider{preview.restorable.length === 1 ? "" : "s"}
            </button>
          ) : (
            <div className="text-[11px] text-text-muted italic" data-testid="rider-snapshot-nothing">Nothing to restore — every rider in this snapshot already exists.</div>
          )}
          <button onClick={() => setPreview(null)} className="w-full text-[10px] uppercase tracking-widest text-text-muted" data-testid="rider-snapshot-cancel-button">Cancel</button>
        </div>
      )}
    </div>
  );
}
